// FLAG:docuAiEnhancements — "Analyse-Problem melden" as ONE component instead of one copy per host.
// The user describes what the analysis got wrong; the text is posted straight to the DMS public API,
// which persists it on the version and forwards it to DocuAI's issue worklist. Fire-and-forget
// for the user — the DMS owns persistence and the best-effort forward.
//
// It lived inline in the document drawer's footer, which meant the expense review surface — where
// a wrong analysis is just as visible — had no way to report anything. Hosts now mount this and get
// the identical button, dialog, error mapping and flag gate. The gate lives INSIDE: a host cannot
// forget it, and switching the flag off removes the entry point everywhere at once.
//
// Deliberately NOT shared with file-toolbar.js: that one reports a DMS *search* problem
// (target 'dms' + searchTerm), a different report with a different payload.
import React, { useRef } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import lang from '@/lang'
import { user } from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { ButtonIconSquare, ButtonSquare, CancelButton, SquareIconButton } from '@/modules/base/components/buttons'
// Deliberately NOT via the '@/modules/base/components/inputs' barrel: it drags in TagSelectInput and
// with it react-tag-input, whose untransformed ESM breaks any isolated jest suite that ends up
// importing this component (embedded-invoice-review-preview-continuity.test.tsx did).
import TextareaInput from '@/modules/base/components/inputs/textarea-input/textarea-input'
import { Title } from '@/modules/base/components/texts'
import { useOpenModal } from '@/modules/_helpers/openModal'
import { toast } from '@/modules/base/components/toast/toastConfig'
import FileService from '@/modules/file/fileService'

const useStyles = makeStyles(() => ({
  modalWrapper: {
    padding: 22,
  },
  button: {
    width: 145,
  },
  title: {
    textAlign: 'left',
  },
}))

type ReportAnalysisProblemButtonProps = {
  /** The document the report is about. Without it there is nothing to report on. */
  minioId?: string,
  customClass?: string,
  /** Optional topbar label; icon-only remains the compact drawer/chat default. */
  label?: string,
  /**
   * Button skin — the same union SquareIconButton accepts, so a typo is a compile error rather
   * than a silently unstyled button. Defaults to 'white' (the drawer footer and the
   * review component, where it sits on a light surface); hosts that put it in the receipt topbar
   * pass 'outline' so it matches the "Speichern"/delete buttons standing next to it.
   */
  variant?: 'primary' | 'white' | 'red' | 'grey' | 'outline' | 'icon',
}

type UseReportAnalysisProblemActionProps = {
  /** The document the report is about. Without it there is nothing to report on. */
  minioId?: string,
}

/**
 * Shared action behind every "Problem melden" entry point.
 *
 * Keeping the feature gate and the dialog in this hook lets compact icon buttons and menu items
 * expose the exact same behavior without duplicating the report flow. Consumers must use
 * `canReport` to omit their entry point when the feature is unavailable.
 */
export const useReportAnalysisProblemAction = ({ minioId }: UseReportAnalysisProblemActionProps) => {
  const classes = useStyles()
  const openModal = useOpenModal()
  // Held in a ref, not state: the modal's action buttons are built once and would otherwise close
  // over the empty initial value (the same closure problem the drawer footer solves this way).
  const reportTextRef = useRef('')

  const canReport = Boolean(
    user?.experimentalFeatures?.includes(experimentalFeatures.docuAiEnhancements) && minioId,
  )

  const onSubmit = (close) => {
    const description = reportTextRef.current.trim()
    if (!description || !minioId) return
    FileService.reportProblem({ minioId, description })
      .then((response) => {
        // `forwarded` says whether DocuAI took this report right away. False is NOT a
        // failure — the DMS has armed the row and its reconciler re-posts until DocuAI
        // accepts it — but claiming plain success would be a lie the user cannot check.
        // The common false case is reporting a problem before the analysis has landed.
        const forwarded = response?.data?.result?.forwarded !== false
        toast.showToast({
          type: 'success',
          message: forwarded
            ? lang.documents.drawer.footer.reportProblemSuccess
            : lang.documents.drawer.footer.reportProblemQueued,
        })
        close()
      })
      .catch((error) => {
        // Network failure / 429 anti-spam cap / generic — the dialog stays open with the text
        // preserved, so the user can retry (FileService maps the message).
        toast.showToast({ type: 'error', message: FileService.getReportProblemErrorMessage(error) })
      })
  }

  const openReportDialog = () => {
    if (!canReport) return
    reportTextRef.current = ''
    openModal(close => ({
      component: 'ModalSmall',
      children: <div className={classes.modalWrapper}>
        <Title className={classes.title} position='center'>{lang.documents.drawer.footer.reportProblemTitle}</Title>
        <TextareaInput
          useFormik={false}
          enableAiFeatures={false}
          name='reportProblem'
          rows={5}
          placeholder={lang.documents.drawer.footer.reportProblemPlaceholder}
          onChange={(value) => { reportTextRef.current = value }}
        />
      </div>,
      props: {
        onBackdropClick: close,
        showCloseIcon: false,
        actions: [
          <CancelButton
            key='cancel'
            type='cancel'
            label={lang.cancelButtonLabel}
            onClick={close}
            className={classes.button}
            dataQsId='reportAnalysisProblem-cancel'
          />,
          <ButtonSquare
            key='submit'
            className={classes.button}
            onClick={() => { onSubmit(close) }}
            label={lang.documents.drawer.footer.reportProblemSubmit}
            dataQsId='reportAnalysisProblem-submit'
          />,
        ],
      },
    }))
  }

  return { canReport, openReportDialog }
}

const ReportAnalysisProblemButton = ({
  minioId,
  customClass,
  label,
  variant = 'white',
}: ReportAnalysisProblemButtonProps) => {
  const theme: ThemeInterface = useTheme()
  const { canReport, openReportDialog } = useReportAnalysisProblemAction({ minioId })

  if (!canReport) { return null }

  return label
    ? (
      <ButtonIconSquare
        tooltip={lang.documents.drawer.footer.reportProblem}
        dataQsId='reportAnalysisProblem'
        customClass={customClass}
        onClick={openReportDialog}
        icon={ReportProblemOutlinedIcon}
        iconWidth='18px'
        iconFill={theme.black}
        label={label}
        variant={variant === 'primary' ? 'fill' : 'outline'}
      />
    )
    : (
      <SquareIconButton
        tooltip={lang.documents.drawer.footer.reportProblem}
        dataQsId='reportAnalysisProblem'
        customClass={customClass}
        onClick={openReportDialog}
        icon={ReportProblemOutlinedIcon}
        size='large'
        iconColor={theme.black}
        variant={variant}
      />
    )
}

export default ReportAnalysisProblemButton
